// src/pages/employer/profile/EmployerTeam.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { orgAPI } from '../../../services/api';
import { AddBtn } from '../../../components/shared/UI';
import { Users, Edit3, X, Trash2 } from 'lucide-react';

const Section = ({ title, children, action }) => (
  <div className="section-box" style={{ marginBottom: 16 }}>
    <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span>{title}</span>
      {action}
    </div>
    <div className="section-box-body">{children}</div>
  </div>
);

const FieldRow = ({ label, value, placeholder = 'Not set' }) => (
  <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', padding: '10px 0',
    borderBottom: '1px solid var(--border)' }}>
    <div style={{ width: 160, flexShrink: 0, fontSize: 11, fontWeight: 700,
      color: 'var(--secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', paddingTop: 2 }}>
      {label}
    </div>
    <div style={{ fontSize: 13, color: value ? 'var(--text)' : 'var(--text-muted)', flex: 1 }}>
      {value || placeholder}
    </div>
  </div>
);

const inputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 6,
  border: '1px solid var(--border)', fontSize: 13,
  background: 'white', color: 'var(--text)', outline: 'none',
  boxSizing: 'border-box',
};
const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--secondary)',
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
};

const HEADCOUNT_OPTIONS = [
  { value: '1-10',       label: '1–10 employees'      },
  { value: '11-50',      label: '11–50 employees'     },
  { value: '51-200',     label: '51–200 employees'    },
  { value: '201-500',    label: '201–500 employees'   },
  { value: '501-1000',   label: '501–1,000 employees' },
  { value: '1001-5000',  label: '1,001–5,000 employees' },
  { value: '5001+',      label: '5,001+ employees'    },
];

const ORG_STRUCTURE_OPTIONS = [
  { value: 'flat',        label: 'Flat'         },
  { value: 'hierarchical',label: 'Hierarchical' },
  { value: 'matrix',      label: 'Matrix'       },
  { value: 'holacratic',  label: 'Holacratic'   },
  { value: 'divisional',  label: 'Divisional'   },
];

// ── Departments editor ────────────────────────────────────
const DepartmentsEditor = ({ departments, onChange }) => {
  const [input, setInput] = useState('');

  const add = () => {
    const v = input.trim();
    if (!v || departments.includes(v)) return;
    onChange([...departments, v]);
    setInput('');
  };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {departments.map((d, i) => (
          <div key={i} className="tag" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {d}
            <Trash2 size={10} style={{ cursor: 'pointer', color: 'var(--text-muted)' }}
              onClick={() => onChange(departments.filter((_, idx) => idx !== i))} />
          </div>
        ))}
        {departments.length === 0 && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            No departments added yet
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input style={{ ...inputStyle, flex: 1 }} value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder="e.g. Engineering, Marketing, Sales…" maxLength={60} />
        <AddBtn onClick={add} size={13} />
      </div>
    </div>
  );
};

// ── Edit form ─────────────────────────────────────────────
const EditForm = ({ profile, onSave, onCancel }) => {
  const [form, setForm] = useState({
    headcountRange: profile?.headcountRange || '',
    orgStructure:   profile?.orgStructure   || '',
    departments:    Array.isArray(profile?.departments) ? profile.departments : [],
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Headcount Range</label>
          <select value={form.headcountRange} onChange={set('headcountRange')} style={inputStyle}>
            <option value="">— Select —</option>
            {HEADCOUNT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Org Structure</label>
          <select value={form.orgStructure} onChange={set('orgStructure')} style={inputStyle}>
            <option value="">— Select —</option>
            {ORG_STRUCTURE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Departments</label>
        <DepartmentsEditor
          departments={form.departments}
          onChange={(v) => setForm((f) => ({ ...f, departments: v }))}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────
export const EmployerTeam = () => {
  const { activeOrg, activeProfile, isAdmin } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeOrg?.id || !activeProfile?.id) { setLoading(false); return; }
    orgAPI.getProfile(activeOrg.id, activeProfile.id)
      .then(({ data }) => setProfile(data.data.profile))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeOrg, activeProfile]);

  const handleSave = async (form) => {
    await orgAPI.updateProfile(activeOrg.id, activeProfile.id, form);
    const { data } = await orgAPI.getProfile(activeOrg.id, activeProfile.id);
    setProfile(data.data.profile);
    setEditing(false);
  };

  const headcountLabel = HEADCOUNT_OPTIONS.find((o) => o.value === profile?.headcountRange)?.label;
  const orgStructureLabel = ORG_STRUCTURE_OPTIONS.find((o) => o.value === profile?.orgStructure)?.label;

  if (!activeOrg) return (
    <div className="fade-in">
      <div className="page-header"><h1 style={{ color: 'var(--secondary)' }}>Team</h1></div>
      <div className="section-box">
        <div className="section-box-body" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Users size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>No organisation found.</p>
        </div>
      </div>
    </div>
  );

  if (loading) return (
    <div className="fade-in">
      <div className="page-header"><h1 style={{ color: 'var(--secondary)' }}>Team</h1></div>
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
    </div>
  );

  const editBtn = isAdmin && (
    <button className="btn btn-ghost btn-sm" onClick={() => setEditing(!editing)}
      style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
      {editing ? <><X size={12} /> Cancel</> : <><Edit3 size={12} /> Edit</>}
    </button>
  );

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1 style={{ color: 'var(--secondary)' }}>Team</h1>
          <p>Share your organisation's structure and size.</p>
        </div>
        {editBtn}
      </div>

      {editing ? (
        <Section title="Edit Team">
          <EditForm profile={profile} onSave={handleSave} onCancel={() => setEditing(false)} />
        </Section>
      ) : (
        <>
          <Section title="Organisation Structure">
            <FieldRow label="Headcount"     value={headcountLabel}     />
            <FieldRow label="Org Structure" value={orgStructureLabel}  />
          </Section>

          <Section title="Departments">
            {Array.isArray(profile?.departments) && profile.departments.length > 0
              ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {profile.departments.map((d, i) => (
                    <span key={i} className="tag">{d}</span>
                  ))}
                </div>
              : <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>
                  No departments added yet.
                </p>
            }
          </Section>
        </>
      )}
    </div>
  );
};
